import React, { useMemo } from 'react';
import { BAND_FILL, bandForScore } from './riskBands';

/**
 * Risk across a customer's engagements, in time order.
 *
 * The question customers actually ask — "is this getting better?" — and the
 * one number that answers it honestly is the mean of exposure × impact, on the
 * same 1–25 scale as the matrix. A total would make a broader assessment look
 * riskier than a narrow one purely for having looked at more.
 *
 * The finding count rides underneath as columns, because a mean that fell
 * while volume doubled is not an improvement and a lone line would let someone
 * present it as one.
 */

const VIEW_W = 1000;
const VIEW_H = 260;
const PAD = { top: 18, right: 20, bottom: 46, left: 34 };
const MAX_RISK = 25;

function RiskHistory({ points = [] }) {
  const plotW = VIEW_W - PAD.left - PAD.right;
  const plotH = VIEW_H - PAD.top - PAD.bottom;

  const { xFor, yFor, maxFindings, line } = useMemo(() => {
    const step = points.length > 1 ? plotW / (points.length - 1) : 0;
    const x = (i) => PAD.left + (points.length === 1 ? plotW / 2 : i * step);
    const y = (risk) => PAD.top + (1 - Math.min(risk, MAX_RISK) / MAX_RISK) * plotH;
    const maxN = Math.max(1, ...points.map((p) => p.findings));

    // Break the line where an engagement produced no estimate, rather than
    // joining across the gap and inventing a trend through it.
    const segments = [];
    let current = [];
    points.forEach((p, i) => {
      if (p.mean_risk == null) {
        if (current.length) segments.push(current);
        current = [];
      } else {
        current.push(`${x(i)},${y(p.mean_risk)}`);
      }
    });
    if (current.length) segments.push(current);

    return { xFor: x, yFor: y, maxFindings: maxN, line: segments };
  }, [points, plotW, plotH]);

  if (!points.length) {
    return (
      <div className="flex items-center justify-center h-48 text-sm text-slate-500">
        Not enough engagements to plot a trend.
      </div>
    );
  }

  return (
    <div>
      <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} className="w-full h-[260px] select-none">
        {[5, 10, 15, 20, 25].map((risk) => (
          <g key={risk}>
            <line
              x1={PAD.left} x2={PAD.left + plotW} y1={yFor(risk)} y2={yFor(risk)}
              className="stroke-slate-800" strokeWidth="1" strokeDasharray="3 5"
            />
            <text x={PAD.left - 6} y={yFor(risk) + 3} textAnchor="end"
                  className="fill-slate-600" style={{ fontSize: '9px' }}>
              {risk}
            </text>
          </g>
        ))}

        {/* Finding volume, behind the line and deliberately quiet: it is
            context for the mean, not a second headline. */}
        {points.map((p, i) => {
          const h = (p.findings / maxFindings) * (plotH * 0.32);
          const w = Math.min(26, plotW / (points.length * 1.8));
          return (
            <rect
              key={`vol-${p.id}`}
              x={xFor(i) - w / 2}
              y={PAD.top + plotH - h}
              width={w}
              height={h}
              className="fill-slate-700/40"
              rx="2"
            >
              <title>{`${p.findings} finding${p.findings === 1 ? '' : 's'}`}</title>
            </rect>
          );
        })}

        {line.map((segment, i) => (
          <polyline
            key={`seg-${i}`}
            points={segment.join(' ')}
            fill="none"
            stroke="#818cf8"
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}

        {points.map((p, i) => {
          if (p.mean_risk == null) return null;
          // Coloured by the band the mean itself falls in, so the point carries
          // its own severity rather than relying on the reader tracing across.
          const band = bandForScore(p.mean_risk);
          return (
            <g key={`pt-${p.id}`}>
              <circle cx={xFor(i)} cy={yFor(p.mean_risk)} r="5"
                      fill={BAND_FILL[band]} stroke="#0f172a" strokeWidth="1.5">
                <title>{`${p.code} — mean risk ${p.mean_risk} · ${p.findings} finding${p.findings === 1 ? '' : 's'}`}</title>
              </circle>
              <text x={xFor(i)} y={yFor(p.mean_risk) - 11} textAnchor="middle"
                    className="fill-slate-300" style={{ fontSize: '10px', fontWeight: 600 }}>
                {p.mean_risk}
              </text>
            </g>
          );
        })}

        {points.map((p, i) => (
          <g key={`lbl-${p.id}`}>
            <text x={xFor(i)} y={VIEW_H - 26} textAnchor="middle"
                  className="fill-slate-400" style={{ fontSize: '9px' }}>
              {p.subtype || ''}
            </text>
            <text x={xFor(i)} y={VIEW_H - 14} textAnchor="middle"
                  className="fill-slate-600" style={{ fontSize: '9px' }}>
              {(p.date || '').slice(0, 7)}
            </text>
          </g>
        ))}
      </svg>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-1 px-1 text-[11px] text-slate-500">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-[3px] rounded-full" style={{ background: '#818cf8' }} />
          Mean risk (exposure × impact, 1–25)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-slate-700/60" />
          Findings
        </span>
        <span className="ml-auto">
          A falling mean beside rising volume is not an improvement.
        </span>
      </div>
    </div>
  );
}

export default RiskHistory;
